import React from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { CATEGORIES } from "@/mock/data";
import { useData } from "@/context/DataContext";
import { rangoPeriodo, eur } from "@/lib/gastos";
import { usePlanes } from "@/lib/ocio";

// Viviendas, Vehículos, Familia y Mascotas cuentan las fichas dadas de alta, no asuntos.
const UNIDADES = { viviendas: ["vivienda", "viviendas"], vehiculos: ["vehículo", "vehículos"], familia: ["familiar", "familiares"], mascotas: ["mascota", "mascotas"], asunto: ["asunto", "asuntos"] };

// Rejilla de áreas grande, táctil y legible (pensada para 50-75 años).
// Reutilizada en Inicio ("¿Qué quieres revisar?") y en la pantalla Áreas.

// Orden de las tarjetas en Mis áreas (elegido por el usuario).
const ORDEN = ["agenda", "contactos", "ocio", "gastos", "familia", "mascotas", "viviendas", "vehiculos", "contratos", "documentos"];
const orden = (id) => ({ order: ORDEN.indexOf(id) });
const TILE = "bg-white border border-[#E5DFD5] rounded-2xl p-5 flex flex-col items-start gap-3 hover:border-[#3A6B56] hover:shadow-md transition-all min-h-[128px] text-left focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/15";
const Tarjeta = ({ id, testid, onClick, Icon, titulo, sub }) => (
  <button data-testid={testid} onClick={onClick} className={TILE} style={orden(id)}>
    <div className="w-14 h-14 rounded-2xl bg-[#E8F0EC] flex items-center justify-center shrink-0"><Icon size={28} className="text-[#3A6B56]" /></div>
    <div className="min-w-0">
      <span className="font-heading font-bold text-lg text-[#1C2421] leading-tight block">{titulo}</span>
      <span className="text-sm text-[#6E7A74]">{sub}</span>
    </div>
  </button>
);

export const AreasGrid = ({ testidPrefix = "area" }) => {
  const navigate = useNavigate();
  const { getCategoryCount, gastos = [], contactos = [] } = useData();
  const proximos = usePlanes().proximos.length;
  const mes = rangoPeriodo("mes");
  const totalMes = gastos.filter((g) => g.fecha >= mes.desde && g.fecha <= mes.hasta).reduce((s, g) => s + g.importe, 0);

  const openArea = (cat) => {
    if (cat.id === "familia") return navigate("/personas");
    if (cat.id === "mascotas") return navigate("/mascotas");
    if (cat.id === "viviendas") return navigate("/viviendas");
    if (cat.id === "vehiculos") return navigate("/vehiculos");
    if (cat.id === "ocio") return navigate("/ocio");
    return navigate(`/area/${cat.id}`);
  };

  return (
    <div className="grid grid-cols-2 gap-3.5" data-testid={`${testidPrefix}-grid`}>
      <Tarjeta id="agenda" testid={`${testidPrefix}-agenda`} onClick={() => navigate("/agenda")} Icon={Icons.CalendarDays}
        titulo="Agenda" sub="Citas, recordatorios, pagos y planes por fecha" />
      {CATEGORIES.map((cat) => {
        const count = getCategoryCount ? getCategoryCount(cat) : 0;
        const Icon = Icons[cat.icon] || Icons.Folder;
        return (
          <button
            key={cat.id}
            data-testid={`${testidPrefix}-${cat.id}`}
            onClick={() => openArea(cat)}
            style={orden(cat.id)}
            className="bg-white border border-[#E5DFD5] rounded-2xl p-5 flex flex-col items-start gap-3 hover:border-[#3A6B56] hover:shadow-md transition-all min-h-[128px] text-left focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/15"
          >
            <div className="w-14 h-14 rounded-2xl bg-[#E8F0EC] flex items-center justify-center shrink-0">
              <Icon size={28} className="text-[#3A6B56]" />
            </div>
            <div className="min-w-0">
              <span className="font-heading font-bold text-lg text-[#1C2421] leading-tight block">
                {cat.label}
              </span>
              <span className="text-sm text-[#6E7A74]">
                {cat.id === "ocio" ? `${proximos} ${proximos === 1 ? "plan próximo" : "planes próximos"}` : <>{count} {(UNIDADES[cat.id] || UNIDADES.asunto)[count === 1 ? 0 : 1]}</>}
              </span>
            </div>
          </button>
        );
      })}
      <button data-testid={`${testidPrefix}-gastos`} onClick={() => navigate("/gastos")} style={orden("gastos")}
        className="bg-white border border-[#E5DFD5] rounded-2xl p-5 flex flex-col items-start gap-3 hover:border-[#3A6B56] hover:shadow-md transition-all min-h-[128px] text-left focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/15">
        <div className="w-14 h-14 rounded-2xl bg-[#E8F0EC] flex items-center justify-center shrink-0"><Icons.Receipt size={28} className="text-[#3A6B56]" /></div>
        <div className="min-w-0">
          <span className="font-heading font-bold text-lg text-[#1C2421] leading-tight block">Gastos</span>
          <span className="text-sm text-[#6E7A74]">Este mes: {eur(totalMes)}</span>
        </div>
      </button>
      <button data-testid={`${testidPrefix}-contactos`} onClick={() => navigate("/contactos")} style={orden("contactos")}
        className="bg-white border border-[#E5DFD5] rounded-2xl p-5 flex flex-col items-start gap-3 hover:border-[#3A6B56] hover:shadow-md transition-all min-h-[128px] text-left focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/15">
        <div className="w-14 h-14 rounded-2xl bg-[#E8F0EC] flex items-center justify-center shrink-0"><Icons.Contact size={28} className="text-[#3A6B56]" /></div>
        <div className="min-w-0">
          <span className="font-heading font-bold text-lg text-[#1C2421] leading-tight block">Contactos</span>
          <span className="text-sm text-[#6E7A74]">{contactos.length} {contactos.length === 1 ? "contacto" : "contactos"}</span>
        </div>
      </button>
      <Tarjeta id="documentos" testid={`${testidPrefix}-documentos`} onClick={() => navigate("/documentos")} Icon={Icons.FileText}
        titulo="Documentos" sub="Facturas, pólizas y archivos guardados" />
    </div>
  );
};
